import { useState, useEffect, useCallback, useMemo } from 'react';
import { studentsAPI } from '../services/api';
import {
  computeDashboardStats,
  fullNameFromStudent,
  collectPlacementRows,
  buildModuleCompletionTrend,
  enrollmentModuleProgressPercent,
  courseStatusFromProgress
} from '../utils/studentDashboardUtils';

/**
 * Loads `Student` record (with enrollments) for the logged-in learner email.
 */
export function useStudentDashboardData(authEmail) {
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!authEmail) {
      setStudent(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await studentsAPI.getByEmail(authEmail.toLowerCase().trim());
      setStudent(res.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setStudent(null);
        setError('not_found');
      } else {
        setStudent(null);
        setError(err.response?.data?.message || err.message || 'Failed to load profile');
      }
    } finally {
      setLoading(false);
    }
  }, [authEmail]);

  useEffect(() => {
    load();
  }, [load]);

  const stats = useMemo(() => computeDashboardStats(student), [student]);

  const primaryEnrollment = useMemo(() => {
    const list = student?.enrollments;
    if (!Array.isArray(list) || list.length === 0) return null;
    return list[0];
  }, [student]);

  const trend = useMemo(
    () => (primaryEnrollment ? buildModuleCompletionTrend(primaryEnrollment) : []),
    [primaryEnrollment]
  );

  const placementRows = useMemo(() => collectPlacementRows(student?.enrollments), [student]);

  const courseCards = useMemo(() => {
    const list = Array.isArray(student?.enrollments) ? student.enrollments : [];
    return list.map((en, idx) => {
      const batch = en.batch;
      const course = batch?.course;
      const pct = enrollmentModuleProgressPercent(en);
      return {
        key: String(en.id ?? idx),
        courseName: course?.courseName || 'Course',
        trainer: '—',
        durationDays: course?.durationDays ?? batch?.duration,
        progress: pct ?? 0,
        status: courseStatusFromProgress(pct),
        enrollmentId: en.id,
        batchName: batch?.batchName
      };
    });
  }, [student]);

  return {
    student,
    loading,
    error,
    reload: load,
    displayName: fullNameFromStudent(student),
    stats,
    primaryEnrollment,
    trend,
    placementRows,
    courseCards
  };
}
